// Generic component for simple policy acknowledgment forms
import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Grid, Divider, Alert, Paper, Checkbox, FormControlLabel } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useAuth } from '../../../../contexts/AuthContext';
import SignaturePad from '../SignaturePad';
import PolicyText, { getFormText, NO_AUTOFILL } from '../PolicyText';

// policyKey picks the legacy policy wording in translation.json
// (employeeForms.policies.<policyKey>).
const SimplePolicyForm = ({ language = 'en', onSubmit, loading, disabled, policyKey }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({ name: '', position: '' });
  const [signature, setSignature] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [errors, setErrors] = useState({});
  const [missingFieldLabels, setMissingFieldLabels] = useState([]);

  useEffect(() => {
    if (user) {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username;
      setFormData(prev => ({ ...prev, name: fullName }));
    }
  }, [user]);

  const labels = language === 'es' ? {
    name: 'Nombre',
    position: 'Puesto',
    signature: 'Firma',
    save: 'Guardar Formulario',
    required: 'Este campo es obligatorio',
    signatureRequired: 'Se requiere firma',
    acknowledge: 'He leído y entiendo esta política, y acepto cumplirla.',
    acknowledgeRequired: 'Debe confirmar que ha leído y entendido la política',
    fixErrors: 'Complete lo siguiente antes de guardar: '
  } : {
    name: 'Name',
    position: 'Position',
    signature: 'Signature',
    save: 'Save Form',
    required: 'This field is required',
    signatureRequired: 'Signature is required',
    acknowledge: 'I have read and understand this policy, and agree to follow it.',
    acknowledgeRequired: 'You must confirm you have read and understood the policy',
    fixErrors: 'Please complete the following before saving: '
  };

  const policyTitle = getFormText(language, `policies.${policyKey}.title`);

  // TKT-277: validation failures used to surface only as small captions next
  // to the field, so pressing Save on a long form looked like nothing had
  // happened. Collect the missing items and list them at the top, matching
  // the pattern the Uniform form uses (TKT-273).
  const validate = () => {
    const newErrors = {};
    const missing = [];
    if (!formData.name.trim()) {
      newErrors.name = labels.required;
      missing.push(labels.name);
    }
    if (!formData.position.trim()) {
      newErrors.position = labels.required;
      missing.push(labels.position);
    }
    if (!acknowledged) {
      newErrors.acknowledged = labels.acknowledgeRequired;
      missing.push(labels.acknowledge);
    }
    if (!signature) {
      newErrors.signature = labels.signatureRequired;
      missing.push(labels.signature);
    }
    setErrors(newErrors);
    setMissingFieldLabels(missing);
    return missing.length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({ ...formData, acknowledged: true }, signature);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} autoComplete="off" data-lpignore="true" data-form-type="other">
      {missingFieldLabels.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          {labels.fixErrors}{missingFieldLabels.join(', ')}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }}>
        <Typography variant="h6" gutterBottom>
          {policyTitle}
        </Typography>
        <PolicyText language={language} policyKey={policyKey} values={{ name: formData.name }} />
      </Paper>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth label={labels.name} name="name" value={formData.name} 
            onChange={(e) => setFormData({...formData, name: e.target.value})} required disabled={disabled} inputProps={NO_AUTOFILL} 
            error={!!errors.name} helperText={errors.name} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth label={labels.position} name="position" value={formData.position} 
            onChange={(e) => setFormData({...formData, position: e.target.value})} required disabled={disabled} inputProps={NO_AUTOFILL} 
            error={!!errors.position} helperText={errors.position} />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* TKT-277: explicit acknowledgement before signing. The policy text told
          staff to review the policy but gave them nothing to affirm it with. */}
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={acknowledged}
              onChange={(e) => {
                setAcknowledged(e.target.checked);
                if (e.target.checked) {
                  setErrors((prev) => ({ ...prev, acknowledged: '' }));
                  setMissingFieldLabels((prev) => prev.filter((l) => l !== labels.acknowledge));
                }
              }}
              disabled={disabled}
              inputProps={{ 'aria-label': labels.acknowledge }}
            />
          }
          label={labels.acknowledge}
        />
        {errors.acknowledged && (
          <Typography color="error" variant="caption" display="block">
            {errors.acknowledged}
          </Typography>
        )}
      </Box>

      <SignaturePad onSignature={(sig) => {
        setSignature(sig);
        if (sig) {
          setErrors((prev) => ({ ...prev, signature: '' }));
          setMissingFieldLabels((prev) => prev.filter((l) => l !== labels.signature));
        }
      }} label={labels.signature} language={language} />
      {errors.signature && <Typography color="error" variant="caption">{errors.signature}</Typography>}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="submit" variant="contained" size="large" startIcon={<SaveIcon />} disabled={disabled || loading}>
          {labels.save}
        </Button>
      </Box>
    </Box>
  );
};

export default SimplePolicyForm;
